import { Injectable, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';

import { documentsMockData } from '../sample/documents-mock-data';
import { findingsMockData } from '../sample/findings-mock-data';
import { aggregateReportMockData } from '../sample/report-cards-mock-data';
import { acknowledgmentsMockData } from '../sample/acknowledgments-mock-data';

// === Types ===

export type FindingStatus =
  | 'validated'
  | 'documented_not_validated'
  | 'recommended_not_documented'
  | 'not_verifiable'
  | 'potentially_verifiable';

export type ControlDomain =
  | 'access_control'
  | 'vulnerability_management'
  | 'endpoint_security'
  | 'identity_authentication'
  | 'incident_response'
  | 'logging_monitoring'
  | 'security_awareness'
  | 'backup_recovery';

// === Interfaces ===

export interface PolicyDocument {
  id: number;
  file_name: string;
  file_type: 'pdf' | 'docx';
  uploaded_at: string;
  uploaded_by: string;
  status: 'uploaded' | 'processing' | 'analyzed' | 'error';
  domains: ControlDomain[];
  statement_count: number;
  report_card: ReportCardSummary | null;
}

export interface ReportCardSummary {
  alignment_label: 'strong' | 'moderate' | 'limited' | 'insufficient_evidence';
  validated_count: number;
  documented_not_validated_count: number;
  recommended_not_documented_count: number;
  not_verifiable_count: number;
  potentially_verifiable_count: number;
  total_findings: number;
}

export interface Finding {
  id: number;
  document_id: number;
  status: FindingStatus;
  domain: ControlDomain;
  title: string;
  original_text: string | null;
  normalized_text: string;
  explanation: string;
  evidence_source: string | null;
  confidence: 'high' | 'medium' | 'low';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'informational';
  source_reference: string | null;
  is_aggregate: boolean;
}

export interface AggregateReport {
  report_card: ReportCardSummary;
  domain_breakdown: DomainBreakdown[];
  last_analysis_date: string;
  total_documents: number;
}

export interface DomainBreakdown {
  domain: ControlDomain;
  display_name: string;
  validated: number;
  documented_not_validated: number;
  recommended_not_documented: number;
  not_verifiable: number;
  potentially_verifiable: number;
  total: number;
}

export interface GapAcknowledgment {
  finding_id: number;
  acknowledged_by: string;
  acknowledged_at: string;
  note: string;
  is_compensating_control: boolean;
  compensating_control_description: string | null;
}

// === Helpers ===

export const FINDING_STATUS_CONFIG: Record<FindingStatus, { label: string; color: string; bootstrapClass: string }> = {
  validated: { label: 'Validated', color: '#28a745', bootstrapClass: 'bg-success' },
  documented_not_validated: { label: 'Not Validated', color: '#dc3545', bootstrapClass: 'bg-danger' },
  recommended_not_documented: { label: 'Recommended', color: '#fd7e14', bootstrapClass: 'bg-warning text-dark' },
  not_verifiable: { label: 'Not Verifiable', color: '#adb5bd', bootstrapClass: 'bg-secondary' },
  potentially_verifiable: { label: 'Needs More Data', color: '#17a2b8', bootstrapClass: 'bg-info' },
};

export const ALIGNMENT_LABEL_CONFIG: Record<string, { label: string; bootstrapClass: string }> = {
  strong: { label: 'Strong Alignment', bootstrapClass: 'bg-success' },
  moderate: { label: 'Moderate Alignment', bootstrapClass: 'bg-warning text-dark' },
  limited: { label: 'Limited Alignment', bootstrapClass: 'bg-danger' },
  insufficient_evidence: { label: 'Insufficient Evidence', bootstrapClass: 'bg-secondary' },
};

export const DOMAIN_DISPLAY_NAMES: Record<ControlDomain, string> = {
  access_control: 'Access Control',
  vulnerability_management: 'Vulnerability Management',
  endpoint_security: 'Endpoint Security',
  identity_authentication: 'Identity & Authentication',
  incident_response: 'Incident Response',
  logging_monitoring: 'Logging & Monitoring',
  security_awareness: 'Security Awareness',
  backup_recovery: 'Backup & Recovery',
};

// === Service ===

@Injectable({
  providedIn: 'root',
})
export class PolicyGapAnalysisService {
  // In-memory acknowledgment state (mock — will be API-backed in production)
  private acknowledgments = signal<Map<number, GapAcknowledgment>>(
    new Map(acknowledgmentsMockData.map((a) => [a.finding_id, a]))
  );

  public getAcknowledgments(): Signal<Map<number, GapAcknowledgment>> {
    return this.acknowledgments.asReadonly();
  }

  public getAcknowledgment(findingId: number): GapAcknowledgment | undefined {
    return this.acknowledgments().get(findingId);
  }

  public acknowledgeFinding(
    findingId: number,
    data: Omit<GapAcknowledgment, 'finding_id' | 'acknowledged_at'>
  ): Observable<GapAcknowledgment> {
    const acknowledgment: GapAcknowledgment = {
      ...data,
      finding_id: findingId,
      acknowledged_at: new Date().toISOString(),
    };
    const updated = new Map(this.acknowledgments());
    updated.set(findingId, acknowledgment);
    this.acknowledgments.set(updated);
    return of(acknowledgment);

    // Production API call
    // return this.http.post<GapAcknowledgment>(`${environment.baseUrl}/v1/findings/${findingId}/acknowledge/`, data);
  }

  public removeAcknowledgment(findingId: number): Observable<void> {
    const updated = new Map(this.acknowledgments());
    updated.delete(findingId);
    this.acknowledgments.set(updated);
    return of(undefined);

    // Production API call
    // return this.http.delete<void>(`${environment.baseUrl}/v1/findings/${findingId}/acknowledge/`);
  }

  /**
   * Fetch all uploaded documents
   */
  public getDocuments(): Observable<PolicyDocument[]> {
    return of(documentsMockData);

    // Production API call
    // return this.http.get<PolicyDocument[]>(`${environment.baseUrl}/v1/customers/{id}/documents/`);
  }

  /**
   * Fetch findings for a specific document
   */
  public getDocumentFindings(documentId: number): Observable<Finding[]> {
    const filtered = findingsMockData.filter((f) => f.document_id === documentId);
    return of(filtered);

    // Production API call
    // return this.http.get<Finding[]>(`${environment.baseUrl}/v1/documents/${documentId}/findings/`);
  }

  /**
   * Fetch all findings across all documents
   */
  public getAllFindings(): Observable<Finding[]> {
    return of(findingsMockData);

    // Production API call
    // return this.http.get<Finding[]>(`${environment.baseUrl}/v1/customers/{id}/findings/`);
  }

  /**
   * Fetch aggregate report across all documents
   */
  public getAggregateReport(): Observable<AggregateReport> {
    return of(aggregateReportMockData);

    // Production API call
    // return this.http.get<AggregateReport>(`${environment.baseUrl}/v1/customers/{id}/aggregate-report/`);
  }
}
